import { DateTime } from "luxon";
import { isEmpty } from "lodash-es";
import {
  AudioRecordingAppEvent,
  AudioRecordingAppEventKind,
  AudioRecordingModelData,
  RecordingDurationFailureReason,
} from "@/store/audio-recordings/types";

/**
 * Minimum duration in milliseconds for a recording to be considered valid.
 * Recordings shorter than this threshold won't produce any audio blobs because
 * chunks are emitted every 1000ms (DEFAULT_CHUNK_INTERVAL_MS).
 */
export const MIN_RECORDING_DURATION_MS = 1000;

/**
 * Result of calculating recording duration from app_events.
 */
export type RecordingDurationResult =
  | { success: true; durationMs: number; startedAt: DateTime; completedAt: DateTime }
  | { success: false; reason: RecordingDurationFailureReason };

/**
 * Typed accessor for app_events from AudioRecordingModelData.
 * The value_json field is loosely typed, so this provides type safety.
 */
export function getAppEvents(modelData: AudioRecordingModelData): AudioRecordingAppEvent[] {
  const valueJson = modelData.value_json as { app_events?: AudioRecordingAppEvent[] } | undefined;
  return valueJson?.app_events ?? [];
}

/**
 * Find the first event of a specific kind from app_events.
 */
export function findAppEvent(
  appEvents: AudioRecordingAppEvent[],
  kind: AudioRecordingAppEventKind
): AudioRecordingAppEvent | undefined {
  return appEvents.find(event => event.kind === kind);
}

/**
 * Parse an ISO datetime string to a Luxon DateTime object.
 * Returns null if parsing fails or results in an invalid DateTime.
 */
function parseDateTime(isoString: string): DateTime | null {
  const dt = DateTime.fromISO(isoString);
  return dt.isValid ? dt : null;
}

/**
 * Calculate the recording duration from app_events.
 * Uses the occurred_at timestamps from started_local and completed_local events.
 */
export function calculateRecordingDuration(
  modelData: AudioRecordingModelData
): RecordingDurationResult {
  const appEvents = getAppEvents(modelData);

  if (isEmpty(appEvents)) {
    return { success: false, reason: RecordingDurationFailureReason.NO_APP_EVENTS };
  }

  const startedEvent = findAppEvent(appEvents, AudioRecordingAppEventKind.STARTED_LOCAL);
  if (!startedEvent) {
    return { success: false, reason: RecordingDurationFailureReason.MISSING_STARTED_EVENT };
  }

  const completedEvent = findAppEvent(appEvents, AudioRecordingAppEventKind.COMPLETED_LOCAL);
  if (!completedEvent) {
    return { success: false, reason: RecordingDurationFailureReason.MISSING_COMPLETED_EVENT };
  }

  const startedAt = parseDateTime(startedEvent.occurred_at);
  const completedAt = parseDateTime(completedEvent.occurred_at);

  if (!startedAt || !completedAt) {
    return { success: false, reason: RecordingDurationFailureReason.INVALID_TIMESTAMPS };
  }

  const durationMs = completedAt.diff(startedAt).as("milliseconds");
  return { success: true, durationMs, startedAt, completedAt };
}

/**
 * Check if a recording is too short to have produced any audio content.
 * This happens when the recording duration is less than MIN_RECORDING_DURATION_MS,
 * meaning no audio chunks were captured before the recording was stopped.
 */
export function isRecordingTooShort(modelData: AudioRecordingModelData): boolean {
  const result = calculateRecordingDuration(modelData);

  if (!result.success) {
    // If we can't determine duration, assume it's not too short
    // (we'll fall back to the generic error)
    return false;
  }

  return result.durationMs < MIN_RECORDING_DURATION_MS;
}
